import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { getDataType } from '../catalog';
import { IntegrationPoint, DataTypeKey } from '../types';

/**
 * What the imported data is actually FOR.
 *
 * The rest of this screen proves data arrived; it never says why that matters. A customer watching
 * a demo sees a row count go up and has to be told the payoff out loud. This panel states it on
 * screen: which engagement drivers now have an input they did not have before.
 *
 * Scope note: this component reads the integration demo state and nothing else, and is rendered
 * only inside the External Integrations screen. It does not touch, wrap or feed any pre-existing
 * screen, and no other surface renders anything from the demo store — a real employee list stays a
 * real employee list.
 *
 * ⚠️ The map below is a FRONTEND COPY of knowledge that lives in `Q12ScoringService`, and Phase 2's
 * not-tracked unit (U6) needs the same mapping on the backend. Two copies will drift; this project
 * has already been bitten by exactly that with the module-id list. When U6 lands, this panel must
 * read the mapping from the API and this constant must go. Tracked as M5 in
 * `docs/superpowers/plans/2026-08-20-external-integrations-phase-2.md`.
 */

/**
 * Which Q12 drivers each data type feeds. Grounded in `Q12ScoringService`'s calculators, not in
 * what would be nice to claim:
 *
 * - `CalculateQ1Async`  — role clarity from RoleAssignment + BusinessRole, goal clarity from Goals
 * - `CalculateQ2Async`  — equipment coverage rows
 * - `CalculateQ3Async`  — strength alignment against the skills roles and goals require
 * - `CalculateQ6Async`  — skill progression from SkillEvidence over UserSkill
 * - `CalculateQ12Async` — skill growth, role changes, goal completion
 *
 * `employees` and `departments` are deliberately empty here: they are not one driver's input, they
 * are the denominator every driver divides by and the breakdown every driver is sliced by. They get
 * their own line rather than being listed under all twelve.
 */
const DRIVERS_BY_DATA_TYPE: Record<DataTypeKey, string[]> = {
  employees: [],
  departments: [],
  jobRoles: ['Q1', 'Q3', 'Q12'],
  goals: ['Q1', 'Q3', 'Q12'],
  skills: ['Q3', 'Q6', 'Q12'],
  assets: ['Q2'],
};

const FOUNDATIONAL: DataTypeKey[] = ['employees', 'departments'];

const DRIVER_ORDER = ['Q1', 'Q2', 'Q3', 'Q6', 'Q12'];

interface ImportImpactPanelProps {
  points: IntegrationPoint[];
}

const ImportImpactPanel: React.FC<ImportImpactPanelProps> = ({ points }) => {
  const { t } = useTranslation();

  // "Imported" is `lastUpdatedOn` being set, which only a completed import writes.
  const importedTypes = useMemo(
    () => points.filter((p) => Boolean(p.lastUpdatedOn)).map((p) => p.dataType),
    [points],
  );

  const foundational = importedTypes.filter((k) => FOUNDATIONAL.includes(k));

  // Driver -> the imported data types feeding it, in catalogue order.
  const driverRows = useMemo(() => {
    return DRIVER_ORDER.map((driver) => ({
      driver,
      feeders: importedTypes.filter((k) => DRIVERS_BY_DATA_TYPE[k].includes(driver)),
    })).filter((row) => row.feeders.length > 0);
  }, [importedTypes]);

  // Nothing imported yet means there is nothing truthful to show.
  if (importedTypes.length === 0) return null;

  return (
    <section
      className="rounded-lg border border-border bg-card p-4"
      data-testid="import-impact-panel"
    >
      <h3 className="text-sm font-semibold text-foreground">{t('extInt.impact.heading')}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{t('extInt.impact.subheading')}</p>

      {foundational.length > 0 && (
        <p className="mt-3 text-sm text-muted-foreground" data-testid="import-impact-foundation">
          {t('extInt.impact.foundation', {
            types: foundational.map((k) => t(getDataType(k).labelKey)).join(', '),
          })}
        </p>
      )}

      {driverRows.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid="import-impact-drivers">
          {driverRows.map(({ driver, feeders }) => (
            <li
              key={driver}
              className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 border-t border-border pt-2 first:border-t-0 first:pt-0"
              data-testid={`import-impact-driver-${driver}`}
            >
              <span className="text-xs font-medium text-primary">{driver}</span>
              <span className="text-sm text-foreground">
                {t(`insights.q12.${driver.toLowerCase()}Name`)}
              </span>
              <span className="text-xs text-muted-foreground">
                {t('extInt.impact.fedBy', {
                  types: feeders.map((k) => t(getDataType(k).labelKey)).join(', '),
                })}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Honest about timing: the drivers are recomputed by a daily job, so the dashboard does not
          move the instant an import finishes. Saying otherwise on a demo screen invites the client
          to click through and find the old number. */}
      <p className="mt-3 text-xs text-muted-foreground" data-testid="import-impact-note">
        {t('extInt.impact.note')}
      </p>

      <Link
        to="/employee-impact/engagement-dashboard"
        className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded"
        data-testid="import-impact-link"
      >
        {t('extInt.impact.viewDashboard')}
        <ArrowRight className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />
      </Link>
    </section>
  );
};

export default ImportImpactPanel;
